import React, { useEffect, useMemo, useState } from "react";
import "../styles.scss";
import ColorSelectionRow from "./ColorSelectionRow";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../redux/actions/user";
import {
  getJetpackConfig,
  useJetpackConfigSelector
} from "../../../redux/jetpackConfig";
import { COLOR_LABELS } from "../../../model/Colors";
import { Colors } from "kadow-jetpack-model";
import {
  DEFAULT_GAME_COLORS,
  DEFAULT_POSA_COLORS
} from "../../../utils/constants";
import { POSATypeJavascriptDTO } from "../../../../../../lib/model/lottery/POSAType";

interface Props {
  isPOSA?: boolean;
  onColorChanged: (colorKey: string, hexColor: string) => void;
  primary?: string;
  accent?: string;
  background?: string;

  titles?: string;
  subtitle?: string;

  posa?: POSATypeJavascriptDTO;
}

const ColorsSelector = (props: Props) => {
  const atd = useAccountToDisplaySelector();
  const dispatch = useDispatch();
  const jetpackConfig = useJetpackConfigSelector();

  const [colorPrimary, setColorPrimary] = useState<string>(
    DEFAULT_GAME_COLORS.titles
  );
  const [colorBackground, setColorBackground] = useState<string>(
    DEFAULT_GAME_COLORS.background
  );
  const [colorAccent, setColorAccent] = useState<string>(
    DEFAULT_GAME_COLORS.accent
  );
  const [colorTitles, setColorTitles] = useState<string>(
    DEFAULT_GAME_COLORS.titles
  );
  const [colorSubtitle, setColorSubtitle] = useState<string>(
    DEFAULT_GAME_COLORS.subtitle
  );

  const loadColors = async () => {
    if (!jetpackConfig) {
      return;
    }

    if (props.isPOSA) {
      if (!!props.background) {
        setColorBackground(props.background);
      } else {
        setColorBackground(DEFAULT_POSA_COLORS.background);
      }

      if (!!props.accent) {
        setColorAccent(props.accent);
      } else {
        setColorAccent(DEFAULT_POSA_COLORS.accent);
      }

      if (!!props.primary) {
        setColorPrimary(props.primary);
      } else {
        setColorPrimary(DEFAULT_POSA_COLORS.primary);
      }

      if (!!props.subtitle) {
        setColorSubtitle(props.subtitle);
      } else {
        setColorSubtitle(DEFAULT_POSA_COLORS.subtitle);
      }

      return;
    }

    if (!jetpackConfig?.colors) {
      setColorPrimary(DEFAULT_GAME_COLORS.primary);
      setColorBackground(DEFAULT_GAME_COLORS.background);
      setColorAccent(DEFAULT_GAME_COLORS.accent);
      setColorTitles(DEFAULT_GAME_COLORS.titles);
      setColorSubtitle(DEFAULT_GAME_COLORS.subtitle);
    }

    if (jetpackConfig?.colors?.primary) {
      setColorPrimary(jetpackConfig?.colors?.primary);
    }

    if (jetpackConfig?.colors?.background) {
      setColorBackground(jetpackConfig?.colors?.background);
    }

    if (jetpackConfig?.colors?.accent) {
      setColorAccent(jetpackConfig?.colors?.accent);
    }

    if (!!props.titles) {
      setColorTitles(props.titles);
    }
  };

  const getLabelNameForColor = (type: keyof Colors): string => {
    const labelSet = props.isPOSA
      ? COLOR_LABELS.jpPOSA
      : COLOR_LABELS.jpMobilePreview;

    return labelSet[type] ?? "";
  };

  const CONTENT = useMemo(() => {
    if (props.isPOSA) {
      return (
        <>
          {!props.posa?.flags?.hideBGColor && (
            <ColorSelectionRow
              isPOSA={props.isPOSA}
              label={getLabelNameForColor("background")}
              color={colorBackground}
              colorKey={"background"}
              onColorChanged={props.onColorChanged}
            />
          )}
          {!props.posa?.flags?.hidePrimaryColor && (
            <ColorSelectionRow
              isPOSA={props.isPOSA}
              label={getLabelNameForColor("primary")}
              color={colorPrimary}
              colorKey={"primary"}
              onColorChanged={props.onColorChanged}
            />
          )}
          {!props.posa?.flags?.hideAccentColor && (
            <ColorSelectionRow
              isPOSA={props.isPOSA}
              label={getLabelNameForColor(
                props.posa?.flags?.renderTitleColorAsBackgroundColor
                  ? "background"
                  : "accent"
              )}
              color={colorAccent}
              colorKey={"accent"}
              onColorChanged={props.onColorChanged}
            />
          )}
          {!props.posa?.flags?.hideSubtitleColor && (
            <ColorSelectionRow
              isPOSA={props.isPOSA}
              label={getLabelNameForColor(
                props.posa?.flags?.renderSubTitleColorAsForegroundColor
                  ? "primary"
                  : "subtitle"
              )}
              color={colorSubtitle}
              colorKey={"subtitle"}
              onColorChanged={props.onColorChanged}
            />
          )}
        </>
      );
    }

    return (
      <>
        <ColorSelectionRow
          label={getLabelNameForColor("background")}
          color={colorBackground}
          colorKey={"background"}
          onColorChanged={props.onColorChanged}
        />
        <ColorSelectionRow
          label={getLabelNameForColor("titles")}
          color={colorTitles}
          colorKey={"titles"}
          onColorChanged={props.onColorChanged}
        />
        <ColorSelectionRow
          label={getLabelNameForColor("accent")}
          color={colorAccent}
          colorKey={"accent"}
          onColorChanged={props.onColorChanged}
        />
      </>
    );
  }, [
    props.isPOSA,
    colorBackground,
    colorAccent,
    colorPrimary,
    colorTitles,
    colorSubtitle
  ]);

  useEffect(() => {
    if (!props.isPOSA) {
      setColorPrimary("");
    }
  }, [props.isPOSA]);

  useEffect(() => {
    void loadColors();
  }, [
    props.primary,
    props.accent,
    props.titles,
    props.background,
    props.subtitle,
    props.isPOSA
  ]);

  useEffect(() => {
    if (!atd?.accountToDisplay) {
      return;
    }
    dispatch(getJetpackConfig(atd?.accountToDisplay));
  }, [atd?.accountToDisplay?.trackingID]);

  return (
    <div className="saas-game-configuration__colors-selector-container">
      {CONTENT}
    </div>
  );
};

export default ColorsSelector;
